import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

const Ctx = createContext({
  jobs: [],
  track: () => {},
  dismiss: () => {},
});

export function useBackgroundJobs() {
  return useContext(Ctx);
}

// Storage key so tracked-jobs survive a full-page reload
const LS_KEY = "sv_bg_jobs_tracked_v1";

function loadTracked() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveTracked(list) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list.slice(0, 20)));
  } catch {}
}

export function BackgroundJobsProvider({ children }) {
  // tracked = jobs this browser session started (id + kind + title)
  const [tracked, setTracked] = useState(loadTracked);
  // live = latest status snapshot per job id
  const [live, setLive] = useState({});
  const trackedRef = useRef(tracked);
  useEffect(() => {
    trackedRef.current = tracked;
    saveTracked(tracked);
  }, [tracked]);

  const track = useCallback((entry) => {
    if (!entry?.id) return;
    setTracked((prev) => {
      const filtered = prev.filter((p) => p.id !== entry.id);
      return [{ ...entry, started_at: entry.started_at || new Date().toISOString(), dismissed: false }, ...filtered];
    });
  }, []);

  const dismiss = useCallback((id) => {
    setTracked((prev) => prev.map((p) => (p.id === id ? { ...p, dismissed: true } : p)));
  }, []);

  // Poll every 3s while any tracked, non-dismissed, non-terminal jobs exist
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const items = trackedRef.current.filter((j) => !j.dismissed);
      if (items.length === 0) return;
      const kinds = new Set(items.map((i) => i.kind));
      const snapshots = {};
      // Batch fetch by kind so we minimize HTTP calls
      try {
        if (kinds.has("verification")) {
          const r = await api.get("/verify/jobs?status=running");
          for (const j of (r.data.items || r.data || [])) snapshots[j.id] = { ...j, _kind: "verification" };
        }
      } catch {}
      try {
        if (kinds.has("leads_upload") || kinds.has("leads_verify")) {
          const r = await api.get("/leads/jobs?active=false");
          for (const j of (r.data.items || [])) snapshots[j.id] = { ...j, _kind: j.filename === "lead-verify" ? "leads_verify" : "leads_upload" };
        }
      } catch {}
      // For any tracked id not in snapshots (endpoint didn't return it), fetch individually
      for (const t of items) {
        if (snapshots[t.id]) continue;
        try {
          const url = t.kind === "verification" ? `/verify/upload/${t.id}` : `/leads/upload/${t.id}`;
          const r = await api.get(url);
          if (r.data) snapshots[t.id] = { ...r.data, _kind: t.kind };
        } catch {}
      }
      if (!cancelled) setLive((prev) => ({ ...prev, ...snapshots }));
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [tracked]);

  // Merge tracked (title, kind) + live (progress) into the exposed list
  const jobs = tracked
    .filter((t) => !t.dismissed)
    .map((t) => mergeJob(t, live[t.id]));

  return <Ctx.Provider value={{ jobs, track, dismiss }}>{children}</Ctx.Provider>;
}

function mergeJob(tracked, entry) {
  if (!entry) {
    return {
      id: tracked.id, kind: tracked.kind, title: tracked.title,
      status: "queued", processed: 0, total: tracked.total || 0,
      updated_at: tracked.started_at,
    };
  }
  return {
    id: tracked.id,
    kind: tracked.kind,
    title: tracked.title,
    status: entry.status,
    processed: entry.processed || 0,
    total: entry.total || tracked.total || 0,
    updated_at: entry.updated_at,
    rate_per_sec: entry.rate_per_sec,
    eta_seconds: entry.eta_seconds,
    summary: entry.summary,
  };
}
